/*
 * The dispute file (owner, 2026-10-31): every proof that a charge was right, for one client and one invoice. Kept out of
 * sight on purpose: the client page's More menu opens it, and an invoice shows DisputeBanner only while Stripe has a
 * dispute open. The letter to the bank is drafted for the owner to change; Send to Stripe goes once (owners only).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { disputeOpen, disputeReason, type DisputeFile, type DisputeRow } from "@steward/shared";
import { api } from "../api/client";
import { useSession } from "../app/session";
import { money, shortDate } from "../components/format";
import { Modal, useToast } from "../components/overlays";
import { Banner, Button, Card, EmptyState, LoadError, Page, Skeleton } from "../components/parts";
import { errText } from "../components/work";
import { Thumb } from "./SocialPosts";

const at = (iso: string) =>
  new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
const day = (d: string) => shortDate(d, 0);

/** Where a dispute stands, in plain words. */
function standing(d: DisputeRow): { type: "warning" | "notice" | "success" | "error"; words: string } {
  if (d.status === "won") return { type: "success", words: "Won. The money stays with you." };
  if (d.status === "lost") return { type: "error", words: "Lost. The money went back to the client." };
  if (d.sentAt)
    return {
      type: "notice",
      words: `Proof sent to Stripe ${at(d.sentAt)}${d.sentByName ? ` by ${d.sentByName}` : ""}. The bank is looking at it.`,
    };
  if (disputeOpen(d.status))
    return { type: "warning", words: `Send your proof${d.dueBy ? ` by ${at(d.dueBy)}` : ""}. Stripe takes it once.` };
  return { type: "notice", words: `Stripe says ${d.status.replace(/_/g, " ")}.` };
}

/** On an invoice page: shows only while Stripe has a dispute on this invoice, for people who can open the file. */
export function DisputeBanner({ invoiceId, clientId }: { invoiceId: string; clientId: string }) {
  const { can, company } = useSession();
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ["dispute-of-invoice", company.id, invoiceId],
    queryFn: () => api<{ dispute: DisputeRow | null }>("GET", `invoices/${invoiceId}/dispute`),
    enabled: can("dispute.read") && !__PREVIEW__,
  });
  const d = q.data?.dispute;
  if (!d) return null;
  const s = standing(d);
  return (
    <Banner
      type={s.type}
      action={
        <Button
          type="secondary"
          size="small"
          onClick={() => void navigate({ to: "/clients/$id/dispute", params: { id: clientId }, search: { invoice: invoiceId } })}
        >
          Open dispute file
        </Button>
      }
    >
      The client {disputeReason(d.reason).words} ({money(d.amountCents)}). {s.words}
    </Banner>
  );
}

async function openPdf(clientId: string, invoiceId: string | null, companyId: string) {
  const w = window.open("", "_blank");
  const res = await fetch(`/v1/clients/${clientId}/dispute-file/pdf${invoiceId ? `?invoice=${invoiceId}` : ""}`, {
    credentials: "same-origin",
    headers: { "x-company-id": companyId },
  });
  if (!res.ok) {
    w?.close();
    throw new Error("The dispute file could not be made.");
  }
  const url = URL.createObjectURL(await res.blob());
  if (w) w.location.href = url;
  else window.location.href = url;
}

export function DisputeFilePage({ clientId, invoiceId }: { clientId: string; invoiceId?: string }) {
  const { can, company } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["dispute-file", company.id, clientId, invoiceId ?? null],
    queryFn: () => api<DisputeFile>("GET", `clients/${clientId}/dispute-file${invoiceId ? `?invoice=${invoiceId}` : ""}`),
  });
  const [letter, setLetter] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const send = useMutation({
    mutationFn: (b: { invoiceId: string; note: string }) => api<DisputeRow>("POST", `clients/${clientId}/dispute-file/send`, b),
    onSuccess: () => {
      setConfirm(false);
      toast("Sent to Stripe");
      void qc.invalidateQueries({ queryKey: ["dispute-file", company.id, clientId] });
      void qc.invalidateQueries({ queryKey: ["dispute-of-invoice", company.id] });
    },
    onError: (e) => toast(errText(e), { kind: "error" }),
  });
  if (q.error)
    return (
      <Page title="Dispute file">
        <LoadError error={q.error} retry={() => void q.refetch()} />
      </Page>
    );
  const f = q.data;
  if (!f)
    return (
      <Page title="Dispute file">
        <Skeleton rows={8} />
      </Page>
    );
  const d = f.dispute;
  const canSend = Boolean(d && disputeOpen(d.status) && !d.sentAt && can("dispute.send") && f.invoice);
  const words = letter ?? d?.note ?? f.summary;
  const pick = (id: string) => void navigate({ to: "/clients/$id/dispute", params: { id: clientId }, search: { invoice: id } });
  return (
    <Page
      title="Dispute file"
      subtitle={f.client.name}
      secondaryAction={
        <Button type="tertiary" icon="arrowLeft" onClick={() => void navigate({ to: "/clients/$id", params: { id: clientId } })}>
          Client
        </Button>
      }
      primaryAction={
        f.invoice && !__PREVIEW__ ? (
          <Button
            type="secondary"
            icon="file"
            onClick={() =>
              void openPdf(clientId, f.invoice!.id, company.id).catch((e: unknown) => toast(errText(e), { kind: "error" }))
            }
          >
            Download PDF
          </Button>
        ) : null
      }
    >
      <div className="stack">
        {f.invoices.length > 1 ? (
          <label className="field">
            <span className="label">Invoice</span>
            <select value={f.invoice?.id ?? ""} onChange={(e) => pick(e.target.value)}>
              {f.invoices.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.number === null ? "Invoice" : `#${String(i.number)}`} {money(i.totalCents)}
                  {i.paidAt ? `, paid ${shortDate(i.paidAt.slice(0, 10))}` : ""}
                  {i.disputed ? ", disputed" : ""}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {!f.invoice ? (
          <EmptyState icon="invoices" title="No card payments yet">
            The file fills in once this client pays an invoice by card.
          </EmptyState>
        ) : null}
        {d ? (
          <Banner type={standing(d).type}>
            <strong>The client {disputeReason(d.reason).words}.</strong> {standing(d).words} {disputeReason(d.reason).proof}
          </Banner>
        ) : null}
        {f.gaps.length ? (
          <Banner type="notice">
            <strong>Weak spots</strong>
            <ul>
              {f.gaps.map((g) => (
                <li key={g}>{g}</li>
              ))}
            </ul>
          </Banner>
        ) : null}

        <Card
          title="Letter to the bank"
          action={
            canSend ? (
              <Button size="small" onClick={() => setConfirm(true)}>
                Send to Stripe
              </Button>
            ) : null
          }
        >
          {canSend ? (
            <label className="field wide">
              <span className="label">Drafted from the file. Change anything that is not right. Short and plain wins.</span>
              <textarea rows={12} value={words} onChange={(e) => setLetter(e.target.value)} />
            </label>
          ) : (
            <p style={{ whiteSpace: "pre-wrap" }}>{words}</p>
          )}
        </Card>

        <Card title="Signed agreement">
          {f.signature ? (
            <div className="stack">
              <p>
                {f.signature.name} signed{" "}
                {f.signature.quoteNumber === null ? "a job confirm" : `quote #${String(f.signature.quoteNumber)}`} on{" "}
                {at(f.signature.at)}
                {f.signature.drawing ? " by hand" : ""}
                {f.signature.ip ? ` from IP ${f.signature.ip}` : ""}.
              </p>
              {f.signature.policies.map((p) => (
                <div key={p.key}>
                  <strong>{p.title}</strong>
                  <p className="muted">{p.text}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="muted">No signed quote or job confirm for this client.</p>
          )}
          {f.confirmedByHand.map((h) => (
            <p key={h.at}>
              Our office marked {h.jobNumber === null ? "the job" : `job #${String(h.jobNumber)}`} confirmed on {at(h.at)}
              {h.by ? ` (${h.by})` : ""}. The client did not sign for it.
            </p>
          ))}
          {f.client.card && f.client.cardSavedAt ? (
            <p>
              {f.client.cardByPhone
                ? `Our office took their ${f.client.card} by phone on ${at(f.client.cardSavedAt)}${f.client.cardTakenBy ? ` (${f.client.cardTakenBy})` : ""}.`
                : `Saved their ${f.client.card} on ${at(f.client.cardSavedAt)}.`}
            </p>
          ) : null}
        </Card>

        {f.visits.length ? (
          <Card title="Visits">
            <div className="stack">
              {f.visits.map((v) => (
                <div key={v.id} className="stack">
                  <strong>
                    {day(v.date)}
                    {v.address ? `, ${v.address}` : ""}
                  </strong>
                  <p className="muted">
                    {v.status === "done" ? "Done" : v.status}
                    {v.completedAt ? `, marked done ${at(v.completedAt)}` : ""}
                    {v.minutes ? `, ${String(v.minutes)} minutes` : ""}
                    {v.checklistDone ? `, ${String(v.checklistDone)} checklist items` : ""}
                  </p>
                  {v.crew.map((c) => (
                    <p key={c.name} className="small">
                      {c.name}
                      {c.clockIn ? `, clocked in ${at(c.clockIn)}` : ""}
                      {c.inFeet !== null ? ` (${String(c.inFeet)} ft from the home)` : ""}
                      {c.clockOut ? `, out ${at(c.clockOut)}` : ""}
                      {c.outFeet !== null ? ` (${String(c.outFeet)} ft)` : ""}
                    </p>
                  ))}
                  {v.photos.length ? (
                    <div className="row wrap">
                      {v.photos.map((p) => (
                        <Thumb key={p.fileId} fileId={p.fileId} alt={`${p.kind} photo`} />
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </Card>
        ) : null}

        <Card title="Payments">
          <div className="stack">
            {f.payments.map((p) => (
              <p key={p.at + String(p.amountCents)}>
                {at(p.at)}, {money(p.amountCents)} {p.card ?? p.method}
                {p.status === "succeeded" ? "" : `, ${p.status}`}
              </p>
            ))}
            <p className="muted">
              {f.pastPaid.count
                ? `${String(f.pastPaid.count)} earlier card charges (${money(f.pastPaid.totalCents)}) paid and never disputed.`
                : "No earlier card charges."}
            </p>
          </div>
        </Card>

        <Card title="Complaints and ratings">
          <div className="stack">
            {!f.complaints.length ? <p>Never told us anything was wrong.</p> : null}
            {f.complaints.map((c) => (
              <div key={c.at}>
                <strong>
                  {at(c.at)}
                  {c.hoursAfter !== null ? `, ${String(c.hoursAfter)} hours after the visit` : ""}
                  {c.inWindow === false ? `, past the ${String(f.complaintHours)} hour window` : ""}
                </strong>
                <p className="muted">{c.what}</p>
              </div>
            ))}
            {f.ratings.map((r) => (
              <p key={r.at}>
                {at(r.at)}, {String(r.stars)} of 5 stars{r.note ? `, "${r.note}"` : ""}
              </p>
            ))}
          </div>
        </Card>

        <Card title={`Messages (${String(f.messages.length)})`}>
          <div className="stack">
            {!f.messages.length ? <p className="muted">No texts or emails for these dates.</p> : null}
            {f.messages.map((m, n) => (
              <div key={String(n)}>
                <strong className="small">
                  {at(m.at)}, {m.from === "client" ? f.client.name : "Us"} by {m.channel === "email" ? "email" : "text"}
                </strong>
                <p className="muted" style={{ whiteSpace: "pre-wrap" }}>
                  {m.body}
                </p>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Modal
        title="Send to Stripe?"
        open={confirm}
        onClose={() => setConfirm(false)}
        footer={
          <>
            <Button type="tertiary" onClick={() => setConfirm(false)}>
              Not yet
            </Button>
            <Button loading={send.isPending} onClick={() => send.mutate({ invoiceId: f.invoice!.id, note: words })}>
              Send to Stripe
            </Button>
          </>
        }
      >
        <p>
          Stripe takes proof once, so check the letter first. This sends the letter, the signed agreement, the visit proof with
          photos, and the messages to the client's bank.
        </p>
      </Modal>
    </Page>
  );
}
